import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import AutoComplete from '@shared/components/AutoComplete';
import Loader from '@shared/components/Loader';
import { getVisitByIdQueryKey } from '../../visits/queries';
import { queryClient } from '../../../shared/queries';
import { Service } from '../entity';
import React from 'react';

type ServiceOption = {
  id: string;
  name: string;
};

type ServicesAutoCompleteProps<TFieldValues extends FieldValues> = {
  fieldPath: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  disabled?: boolean;
  onChange?: (service: ServiceOption | null) => void;
  queryData?: Service[] | undefined;
  isLoading: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
};

const ServicesAutoComplete = <TFieldValues extends FieldValues>(
  props: ServicesAutoCompleteProps<TFieldValues>,
) => {
  const { control, fieldPath, disabled, onChange, queryData, isLoading, inputRef } = props;
  const { visitId } = useParams();
  const intl = useIntl();

  if (!queryData && isLoading) {
    return <Loader />;
  }
  if (!queryData) {
    return (
      <FormattedMessage defaultMessage="Služby se nepodařilo načíst." id="services.notFound" />
    );
  }

  const options = queryData.map(service => ({ id: service.id, name: service.serviceName }));

  return (
    <AutoComplete
      options={options}
      getOptionLabel={o => o.name}
      getOptionValue={o => o.id}
      required={true}
      control={control}
      fieldPath={fieldPath}
      disabled={disabled}
      inputRef={inputRef}
      label={intl.formatMessage({
        id: 'services.chooseService',
        defaultMessage: 'Vyberte službu',
      })}
      placeholder={intl.formatMessage({
        id: 'services.search',
        defaultMessage: 'Hledejte...',
      })}
      onChange={(_, service) => {
        queryClient.invalidateQueries({ queryKey: getVisitByIdQueryKey(visitId) });
        onChange?.(service);
      }}
    />
  );
};

export default ServicesAutoComplete;
